"use client";

import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Globe,
  MapPin,
  Code,
  Tag,
  CheckCircle,
  XCircle,
  Clock,
  Save,
} from "lucide-react";

interface Destination {
  id: string;
  name: string;
  code: string;
  region: string;
  country: string;
  status: "active" | "inactive" | "pending";
  description?: string;
  timezone?: string;
  currency?: string;
  language?: string;
  popularDestinations?: string[];
  created_at: string;
  updated_at: string;
}

const mockDestinations: Destination[] = [
  {
    id: "1",
    name: "Thailand",
    code: "TH",
    region: "Southeast Asia",
    country: "Thailand",
    status: "active",
    description: "Land of smiles with rich culture, beautiful beaches, and vibrant cities",
    timezone: "UTC+7",
    currency: "THB",
    language: "Thai",
    popularDestinations: ["Bangkok", "Phuket", "Chiang Mai", "Krabi"],
    created_at: "2024-01-01",
    updated_at: "2024-08-13",
  },
  {
    id: "2",
    name: "Indonesia",
    code: "ID",
    region: "Southeast Asia",
    country: "Indonesia",
    status: "active",
    description: "Archipelago nation with diverse cultures and stunning natural beauty",
    timezone: "UTC+7",
    currency: "IDR",
    language: "Indonesian",
    popularDestinations: ["Bali", "Jakarta", "Yogyakarta", "Lombok"],
    created_at: "2024-01-01",
    updated_at: "2024-08-13",
  },
  {
    id: "3",
    name: "Vietnam",
    code: "VN",
    region: "Southeast Asia",
    country: "Vietnam",
    status: "active",
    description: "Country with rich history, delicious cuisine, and breathtaking landscapes",
    timezone: "UTC+7",
    currency: "VND",
    language: "Vietnamese",
    popularDestinations: ["Hanoi", "Ho Chi Minh City", "Halong Bay", "Hoi An"],
    created_at: "2024-01-01",
    updated_at: "2024-08-13",
  },
  {
    id: "4",
    name: "Singapore",
    code: "SG",
    region: "Southeast Asia",
    country: "Singapore",
    status: "active",
    description: "Modern city-state known for its cleanliness, efficiency, and multiculturalism",
    timezone: "UTC+8",
    currency: "SGD",
    language: "English, Malay, Mandarin, Tamil",
    popularDestinations: ["Marina Bay", "Sentosa Island", "Chinatown", "Little India"],
    created_at: "2024-01-01",
    updated_at: "2024-08-13",
  },
  {
    id: "5",
    name: "Malaysia",
    code: "MY",
    region: "Southeast Asia",
    country: "Malaysia",
    status: "active",
    description: "Diverse country with modern cities, ancient rainforests, and beautiful islands",
    timezone: "UTC+8",
    currency: "MYR",
    language: "Malay",
    popularDestinations: ["Kuala Lumpur", "Penang", "Langkawi", "Malacca"],
    created_at: "2024-01-01",
    updated_at: "2024-08-13",
  },
  {
    id: "6",
    name: "Sri Lanka",
    code: "LK",
    region: "South Asia",
    country: "Sri Lanka",
    status: "active",
    description: "Teardrop island with rich heritage, tea plantations, and pristine beaches",
    timezone: "UTC+5:30",
    currency: "LKR",
    language: "Sinhala, Tamil",
    popularDestinations: ["Colombo", "Kandy", "Galle", "Sigiriya"],
    created_at: "2024-01-01",
    updated_at: "2024-08-13",
  },
];

const regions = [
  "Southeast Asia",
  "South Asia",
  "East Asia",
  "Central Asia",
  "Middle East",
  "Europe",
  "Africa",
  "North America",
  "South America",
  "Oceania",
];

const statuses = ["active", "inactive", "pending"];

export default function SettingsPage() {
  const { toast } = useToast();
  const [destinations, setDestinations] = React.useState<Destination[]>(mockDestinations);
  const [filteredDestinations, setFilteredDestinations] = React.useState<Destination[]>(mockDestinations);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [regionFilter, setRegionFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editingDestination, setEditingDestination] = React.useState<Destination | null>(null);
  const [formData, setFormData] = React.useState<Partial<Destination>>({
    name: "",
    code: "",
    region: "",
    country: "",
    status: "active",
    description: "",
    timezone: "",
    currency: "",
    language: "",
    popularDestinations: [],
  });

  React.useEffect(() => {
    let filtered = destinations.filter((destination) => {
      const matchesSearch =
        destination.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        destination.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        destination.country.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = regionFilter === "all" || destination.region === regionFilter;
      const matchesStatus = statusFilter === "all" || destination.status === statusFilter;

      return matchesSearch && matchesRegion && matchesStatus;
    });
    setFilteredDestinations(filtered);
  }, [destinations, searchTerm, regionFilter, statusFilter]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddDestination = () => {
    if (!formData.name || !formData.code || !formData.region || !formData.country) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const newDestination: Destination = {
      id: Date.now().toString(),
      name: formData.name,
      code: formData.code.toUpperCase(),
      region: formData.region,
      country: formData.country,
      status: formData.status || "active",
      description: formData.description,
      timezone: formData.timezone,
      currency: formData.currency,
      language: formData.language,
      popularDestinations: formData.popularDestinations || [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    setDestinations(prev => [...prev, newDestination]);
    setFormData({
      name: "",
      code: "",
      region: "",
      country: "",
      status: "active",
      description: "",
      timezone: "",
      currency: "",
      language: "",
      popularDestinations: [],
    });
    setIsAddDialogOpen(false);

    toast({
      title: "Destination Added",
      description: `${newDestination.name} has been added successfully.`,
    });
  };

  const handleEditDestination = () => {
    if (!editingDestination || !formData.name || !formData.code || !formData.region || !formData.country) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const updatedDestination: Destination = {
      ...editingDestination,
      name: formData.name,
      code: formData.code.toUpperCase(),
      region: formData.region,
      country: formData.country,
      status: formData.status || "active",
      description: formData.description,
      timezone: formData.timezone,
      currency: formData.currency,
      language: formData.language,
      popularDestinations: formData.popularDestinations || [],
      updated_at: new Date().toISOString(),
    };

    setDestinations(prev => prev.map(d => d.id === editingDestination.id ? updatedDestination : d));
    setEditingDestination(null);
    setFormData({
      name: "",
      code: "",
      region: "",
      country: "",
      status: "active",
      description: "",
      timezone: "",
      currency: "",
      language: "",
      popularDestinations: [],
    });
    setIsEditDialogOpen(false);

    toast({
      title: "Destination Updated",
      description: `${updatedDestination.name} has been updated successfully.`,
    });
  };

  const handleDeleteDestination = (destination: Destination) => {
    if (confirm(`Are you sure you want to delete ${destination.name}?`)) {
      setDestinations(prev => prev.filter(d => d.id !== destination.id));
      toast({
        title: "Destination Deleted",
        description: `${destination.name} has been removed successfully.`,
      });
    }
  };

  const openEditDialog = (destination: Destination) => {
    setEditingDestination(destination);
    setFormData({
      name: destination.name,
      code: destination.code,
      region: destination.region,
      country: destination.country,
      status: destination.status,
      description: destination.description,
      timezone: destination.timezone,
      currency: destination.currency,
      language: destination.language,
      popularDestinations: destination.popularDestinations,
    });
    setIsEditDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case "inactive":
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Inactive</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Platform Settings</h1>
        <p className="text-gray-600">Manage platform-wide configurations and master data</p>
      </div>

      {/* Destinations Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Destinations Management</CardTitle>
                <CardDescription>
                  Manage destination entries used across the platform for package creation and search filters
                </CardDescription>
              </div>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add Destination
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New Destination</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Destination Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="e.g., Thailand"
                      />
                    </div>
                    <div>
                      <Label htmlFor="code">Country Code *</Label>
                      <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => handleInputChange("code", e.target.value)}
                        placeholder="e.g., TH"
                        maxLength={3}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="region">Region *</Label>
                      <Select value={formData.region} onValueChange={(value) => handleInputChange("region", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent>
                          {regions.map((region) => (
                            <SelectItem key={region} value={region}>{region}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => handleInputChange("country", e.target.value)}
                        placeholder="e.g., Thailand"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      placeholder="Brief description of the destination"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="timezone">Timezone</Label>
                      <Input
                        id="timezone"
                        value={formData.timezone}
                        onChange={(e) => handleInputChange("timezone", e.target.value)}
                        placeholder="e.g., UTC+7"
                      />
                    </div>
                    <div>
                      <Label htmlFor="currency">Currency</Label>
                      <Input
                        id="currency"
                        value={formData.currency}
                        onChange={(e) => handleInputChange("currency", e.target.value)}
                        placeholder="e.g., THB"
                        maxLength={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="language">Language</Label>
                      <Input
                        id="language"
                        value={formData.language}
                        onChange={(e) => handleInputChange("language", e.target.value)}
                        placeholder="e.g., Thai"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => handleInputChange("status", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddDestination}>
                    <Save className="h-4 w-4 mr-2" />
                    Add Destination
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search destinations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map((region) => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Destinations Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destination</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDestinations.map((destination) => (
                  <TableRow key={destination.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{destination.name}</div>
                        <div className="text-sm text-gray-500">{destination.country}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {destination.code}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {destination.region}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        {destination.timezone && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-400" />
                            {destination.timezone}
                          </div>
                        )}
                        {destination.currency && (
                          <div className="flex items-center gap-1">
                            <Tag className="h-3 w-3 text-gray-400" />
                            {destination.currency}
                          </div>
                        )}
                        {destination.language && (
                          <div className="flex items-center gap-1">
                            <Code className="h-3 w-3 text-gray-400" />
                            {destination.language}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(destination.status)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500">
                        {new Date(destination.updated_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(destination)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteDestination(destination)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Summary */}
          <div className="mt-4 text-sm text-gray-500">
            Showing {filteredDestinations.length} of {destinations.length} destinations
          </div>
        </CardContent>
      </Card>

      {/* Edit Destination Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Destination</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">Destination Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Thailand"
                />
              </div>
              <div>
                <Label htmlFor="edit-code">Country Code *</Label>
                <Input
                  id="edit-code"
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value)}
                  placeholder="e.g., TH"
                  maxLength={3}
                  />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-region">Region *</Label>
                <Select value={formData.region} onValueChange={(value) => handleInputChange("region", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((region) => (
                      <SelectItem key={region} value={region}>{region}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
                              <div>
                  <Label htmlFor="edit-country">Country *</Label>
                  <Input
                    id="edit-country"
                    value={formData.country}
                    onChange={(e) => handleInputChange("country", e.target.value)}
                    placeholder="e.g., Thailand"
                  />
                </div>
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="Brief description of the destination"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit-timezone">Timezone</Label>
                <Input
                  id="edit-timezone"
                  value={formData.timezone}
                  onChange={(e) => handleInputChange("timezone", e.target.value)}
                  placeholder="e.g., UTC+7"
                />
              </div>
              <div>
                <Label htmlFor="edit-currency">Currency</Label>
                <Input
                  id="edit-currency"
                  value={formData.currency}
                  onChange={(e) => handleInputChange("currency", e.target.value)}
                  placeholder="e.g., THB"
                  maxLength={3}
                />
              </div>
              <div>
                <Label htmlFor="edit-language">Language</Label>
                <Input
                  id="edit-language"
                  value={formData.language}
                  onChange={(e) => handleInputChange("language", e.target.value)}
                  placeholder="e.g., Thai"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleInputChange("status", value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditDestination}>
              <Save className="h-4 w-4 mr-2" />
              Update Destination
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
